import React, { ReactNode, useCallback, useRef } from 'react';
import { buildClassName } from 'utils/build-class-name';
import LoadingSkeleton from 'components/loading-skeleton';
import styles from './style.module.scss';
import {
  GTM_CLICK_ADDRESS_PAGE_COMPARABLE_LISTING_BOTTOM,
  GTM_CLICK_APPRAISAL_NO_ESTIMATE_SIMILAR_AVAILABLE_CARD,
  GTM_CLICK_APPRAISAL_NO_ESTIMATE_SIMILAR_SOLD_CARD,
  GTM_CLICK_APPRAISAL_SIMILAR_AVAILABLE_CARD,
  GTM_CLICK_APPRAISAL_SIMILAR_SOLD_CARD,
  GTM_CLICK_LISTING_SIMILAR,
} from 'constants/events';
import { useIsMobile } from 'hooks/use-size-class';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { Trans, useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { ListingPriceData } from 'components/listing-price';
import SimilarListingsCarousel from '../similar-listings-carousel';
import SimilarListingsGrid from '../similar-listings-grid';
import { AnalyticsController } from 'analytics/analytics-controller';
import { useIsomorphicLayoutEffect } from 'types/react';
import { SimilarListingsComponentUsage } from './similar-listings.type';

import type { ListingCardProps } from 'components/listing-card/listing-card-v2';
import type { SimilarListing, ComparableListing } from './similar-listings.type';

//#region Constants

const LOADING_SKELETON_DEFAULT_HEIGHT = '396px' as const;
const LOADING_SKELETON_DEFAULT_WIDTH = '370px' as const;

export const LOADING_SKELETON_DEFAULT_COUNT = 3 as const;
//#endregion


//#region Types

export type SimilarListingsProps = {
  /**
   * The heading of the similar listings section. If a ReactNode is provided, it will be rendered as is.
   *
   * @example
   * ```typescript
   * <SimilarListings heading="Similar Listings" />
   * ```
   *
   * @example
   * ```typescript
   * <SimilarListings heading={<span>Similar Listings</span>} />
   * ```
   */
  heading: string | ReactNode;
  /**
   * The listings to display in the similar listings section.
   */
  listings: SimilarListing[];
  /**
   * The component usage.
   */
  componentUsage: SimilarListingsComponentUsage;
  /**
   * Whether the listings are currently loading. Defaults to false. If true, a skeleton will be displayed instead of the listings.
   */
  isLoading?: boolean;
  /**
   * The listing to compare to the similar listings.
   */
  listingToCompare?: ComparableListing;
  /** Whether the listings are for a home appraisal. */
  noEstimate?: boolean;
  /** Whether to show the carousel. */
  showCarousel?: boolean;
  /** Whether to show the price range. */
  showPriceRange?: boolean;
  /**
   * Number of skeleton items to display while loading. Defaults to {@link LOADING_SKELETON_DEFAULT_COUNT 3}.
   */
  skeletonCount?: number;
  /**
   * The function to call when a listing card is clicked.
   */
  onListingCardClick: ListingCardProps['onListingCardClick'];
};
//#endregion

//#region Internal components

function SkeletonCard() {
  return (<LoadingSkeleton height={LOADING_SKELETON_DEFAULT_HEIGHT} width={LOADING_SKELETON_DEFAULT_WIDTH} />);
}

function SimilarListingsCarouselSkeleton() {
  return <LoadingSkeleton height={LOADING_SKELETON_DEFAULT_HEIGHT} />;
}

function SimilarListingsGridSkeleton({ size = LOADING_SKELETON_DEFAULT_COUNT }: { size?: number }) {
  return (
    <div data-testid="similar-listings" className={styles['similar-listings']}>
      {Array.from({ length: size }, (_, index) => (
        <div className={styles['card-wrapper']} key={index}>
          <SkeletonCard />
        </div>
      ))}
    </div>
  );
}

function PriceRangeMobile({ minPrice, maxPrice, componentUsage }: { minPrice: string; maxPrice: string; componentUsage: SimilarListingsComponentUsage }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const priceRangeI18nKey = componentUsage === SimilarListingsComponentUsage.HomeAppraisalAvailable ?
    LOCALE_COMMON_PATHS.ADDRESS_SIMILAR_LISTINGS_PRICE_RANGE_FOR_SALE :
    LOCALE_COMMON_PATHS.ADDRESS_SIMILAR_LISTINGS_PRICE_RANGE_SOLD;
  return (
    <h4 className={styles['price-range']}>
      <Trans
        t={t}
        i18nKey={priceRangeI18nKey}
        values={{ minPrice, maxPrice }}
        components={{ span: <span className={styles['bold-price']} /> }}
      />
    </h4>
  );
}

//#endregion


//#region Component
export default function SimilarListings(props: SimilarListingsProps) {

  const { heading, listings, isLoading = false, listingToCompare, noEstimate, showCarousel, componentUsage, showPriceRange, skeletonCount = LOADING_SKELETON_DEFAULT_COUNT, onListingCardClick } = props;
  const isMobile = useIsMobile();
  const { formatCurrency } = useI18nFormatting();
  const isHomeAppraisalForSale = componentUsage === SimilarListingsComponentUsage.HomeAppraisalAvailable;
  let minPrice: string, maxPrice: string;
  const onListingCardClickRef = useRef<ListingCardProps['onListingCardClick']>(onListingCardClick);

  useIsomorphicLayoutEffect(() => {
    onListingCardClickRef.current = onListingCardClick;
  }, [onListingCardClick]);

  if (showPriceRange) {
    const prices = getMinAndMaxPrice(listings, isHomeAppraisalForSale, formatCurrency);
    minPrice = prices[0];
    maxPrice = prices[1];
  }

  const handleListingCardClick: ListingCardProps['onListingCardClick'] = useCallback((source, data, ...args) => {
    let gtmEventName = '';
    switch (componentUsage) {
      case SimilarListingsComponentUsage.Similar:
        gtmEventName = GTM_CLICK_LISTING_SIMILAR;
        break;
      case SimilarListingsComponentUsage.Comparable:
        gtmEventName = GTM_CLICK_ADDRESS_PAGE_COMPARABLE_LISTING_BOTTOM;
        break;
      case SimilarListingsComponentUsage.HomeAppraisalAvailable:
        gtmEventName = noEstimate ? GTM_CLICK_APPRAISAL_NO_ESTIMATE_SIMILAR_AVAILABLE_CARD : GTM_CLICK_APPRAISAL_SIMILAR_AVAILABLE_CARD;
        break;
      case SimilarListingsComponentUsage.HomeAppraisalSold:
        gtmEventName = noEstimate ? GTM_CLICK_APPRAISAL_NO_ESTIMATE_SIMILAR_SOLD_CARD : GTM_CLICK_APPRAISAL_SIMILAR_SOLD_CARD;
        break;
      default:
        gtmEventName = '';
    }
    onListingCardClickRef.current?.(source, data, ...args);
    AnalyticsController.trackEvent(gtmEventName);
  }, [componentUsage, noEstimate]);

  return (
    <section>
      <h3 className={styles.heading}>{heading}</h3>
      {isMobile && showPriceRange &&
        <PriceRangeMobile minPrice={minPrice} maxPrice={maxPrice} componentUsage={componentUsage} />
      }
      {!showCarousel && (
        isLoading ?
          <SimilarListingsGridSkeleton size={skeletonCount} />
          :
          <SimilarListingsGrid
            listings={listings}
            listingToCompare={listingToCompare}
            onListingCardClick={handleListingCardClick}
          />
      )}
      <div className={buildClassName(showCarousel ? undefined : styles['mobile-carousel'])}>
        {showCarousel && (
          isLoading ?
            <SimilarListingsCarouselSkeleton />
            :
            <SimilarListingsCarousel
              listings={listings}
              listingToCompare={listingToCompare}
              onListingCardClick={handleListingCardClick}
            />
        )}
      </div>
    </section>
  );
}
//#endregion

//#region Helper functions

/**
 * This function iterates through the provided listing price data array and determines the minimum
 * and maximum prices based on the forSale parameter. If forSale is true, it uses the price property;
 * if false, it uses the soldPrice property. The function handles null/undefined listings by skipping them.
 *
 * @param listingPrices Array of price data for each listing
 * @param forSale Boolean flag indicating whether to use listing price (true) or sold price (false)
 * @param formatCurrency Function to format the price as currency
 * @returns Array containing [minPrice, maxPrice] formatted as currency strings
 * @example
 * ```typescript
 * const listings = [
 *   { price: 500000, soldPrice: 450000 },
 *   { price: 750000, soldPrice: 700000 },
 *   { price: 600000, soldPrice: 550000 },
 * ];
 *
 * const prices = getMinAndMaxPrice(listings, true, formatCurrency);
 * // Returns: ['$500,000', '$750,000'] or ['500 000 $ CA', '750 000 $ CA'] depending on locale
 * ```
 */
function getMinAndMaxPrice(
  listingPrices: Pick<ListingPriceData, 'price' | 'soldPrice'>[],
  forSale: boolean,
  formatCurrency: (value: number) => string
) {
  let minPrice: number, maxPrice: number;

  for (const priceData of listingPrices) {
    if (!priceData) continue;
    const listingPrice = forSale ? priceData.price : priceData.soldPrice;
    if (!minPrice || listingPrice < minPrice) {
      minPrice = listingPrice;
    }
    if (!maxPrice || listingPrice > maxPrice) {
      maxPrice = listingPrice;
    }
  }

  return [formatCurrency(minPrice), formatCurrency(maxPrice)];
}
//#endregion